import * as React from 'react';
import { useCallback } from 'react';
import i18next from 'i18next';
import { useListContext } from 'react-admin';
import api, { apiSetup } from 'config/api';
import { countryColor, OTHER_COUNTRY } from 'constants/countryColors';
import { THEME_COLORS, subthemeColor } from 'constants/chartColors';
import { totalsQuery } from 'app/hooks/useActionTotals';
import { useCachedResource } from 'app/hooks/useCachedResource';
import ThemeDonutChart from '~screens/Home/components/ThemeDonutChart';
import ChartCard from '../ChartCard';
import MetricBarChart from '../MetricBarChart';
import { Analytics, Bucket, Metric, donutItems, metricValue } from '../../items';
import '../../i18n';
import styles from './styles.module.scss';

apiSetup(undefined);

const NO_DATA: Analytics = { countries: [], territories: [], themes: [], terms: [], scopes: [], types: [] };
const cache = new Map<string, Analytics>();

// Along the corridor, west to east, as on the Home.
const COUNTRY_ORDER = ['CL', 'AR', 'PY', 'BR'];
const TYPE_COLOR = countryColor('PY').fill;

const countryRank = (bucket: Bucket) => {
  const code = bucket.code || bucket.countries?.[0]?.code || '';
  const rank = COUNTRY_ORDER.indexOf(code);
  return rank === -1 ? COUNTRY_ORDER.length : rank;
};

const byCorridor = (buckets: Bucket[]) =>
  [...buckets].sort((a, b) => countryRank(a) - countryRank(b) || (a.name || '').localeCompare(b.name || ''));

const largestFirst = (buckets: Bucket[], metric: Metric) =>
  [...buckets].sort((a, b) => metricValue(b, metric) - metricValue(a, metric));

/** Without a value the bucket is gray, whatever its position. */
const paletteColor = (buckets: Bucket[], color: (index: number) => string) => (index: number) =>
  buckets[index].id === null ? OTHER_COUNTRY.fill : color(index);

// The list filter that a click on a bucket sets, and the filter input to show for it.
const FILTERS: Record<string, { param: string; input: string; list?: boolean }> = {
  countries: { param: 'countries', input: 'countries', list: true },
  territories: { param: 'territory', input: 'territory' },
  themes: { param: 'theme', input: 'themes' },
  terms: { param: 'term', input: 'term' },
  scopes: { param: 'scope', input: 'scope' },
  types: { param: 'type', input: 'type' }
};

/** The charts of the Acciones matching the list's filters; clicking a bar or slice filters by it. */
const AnalyticsCharts = () => {
  const { filterValues, displayedFilters, setFilters, total } = useListContext();
  const query = totalsQuery(filterValues);
  const analytics = useCachedResource(cache, query, () => api.get<Analytics>(`/actions/analytics/${query}`), NO_DATA);
  const data = analytics || NO_DATA;

  const select = useCallback((dimension: keyof Analytics, id: number | string | null) => {
    if (typeof id !== 'number') {
      return;
    }
    const { param, input, list } = FILTERS[dimension];
    // A new Tema leaves any Subtema of the previous one behind.
    const { subtheme: _, ...rest } = filterValues;
    setFilters(
      { ...(dimension === 'themes' ? rest : filterValues), [param]: list ? [id] : id },
      { ...displayedFilters, [input]: true }
    );
  }, [filterValues, displayedFilters, setFilters]);

  const selectCountry = useCallback((bucket: Bucket) => select('countries', bucket.id), [select]);
  const selectTerritory = useCallback((bucket: Bucket) => select('territories', bucket.id), [select]);
  const selectType = useCallback((bucket: Bucket) => select('types', bucket.id), [select]);
  const selectTheme = useCallback((id: number | string) => select('themes', id), [select]);
  const selectTerm = useCallback((id: number | string) => select('terms', id), [select]);
  const selectScope = useCallback((id: number | string) => select('scopes', id), [select]);

  const countryColorOf = useCallback((bucket: Bucket) => countryColor(bucket.code).fill, []);
  const territoryColorOf = useCallback((bucket: Bucket) => countryColor(bucket.countries?.[0]?.code).fill, []);
  const typeColorOf = useCallback((bucket: Bucket) => (bucket.id === null ? OTHER_COUNTRY.fill : TYPE_COLOR), []);

  const countries = byCorridor(data.countries);
  const territories = byCorridor(data.territories);
  const themeColors = paletteColor(data.themes, i => THEME_COLORS[i % THEME_COLORS.length]);
  const termColors = paletteColor(data.terms, subthemeColor);
  const scopeColors = paletteColor(data.scopes, subthemeColor);

  return (
    <div className={styles.content}>
      <p className={styles.lead}>{i18next.t('analytics:lead', { total: total ?? '...' })}</p>
      <div className={styles.charts} aria-busy={analytics === null}>
        <ChartCard title={i18next.t('analytics:byCountry')} switchable>
          {metric => (
            <MetricBarChart buckets={countries} metric={metric} orientation="vertical" emptyLabel=""
              colorOf={countryColorOf} onSelect={selectCountry} />
          )}
        </ChartCard>
        <ChartCard title={i18next.t('analytics:byTerritory')} switchable>
          {metric => (
            <MetricBarChart buckets={territories} metric={metric} orientation="horizontal" emptyLabel=""
              colorOf={territoryColorOf} onSelect={selectTerritory} />
          )}
        </ChartCard>
        <ChartCard title={i18next.t('analytics:byTheme')} switchable>
          {metric => <ThemeDonutChart items={donutItems(data.themes, metric, '', themeColors)} onSelect={selectTheme} />}
        </ChartCard>
        <ChartCard title={i18next.t('analytics:byTerm')} switchable>
          {metric => (
            <ThemeDonutChart items={donutItems(data.terms, metric, i18next.t('analytics:noTerm'), termColors)}
              onSelect={selectTerm} />
          )}
        </ChartCard>
        <ChartCard title={i18next.t('analytics:byScope')} switchable={false}>
          {metric => (
            <ThemeDonutChart items={donutItems(data.scopes, metric, i18next.t('analytics:noScope'), scopeColors)}
              onSelect={selectScope} />
          )}
        </ChartCard>
        <ChartCard title={i18next.t('analytics:byType')} switchable={false}>
          {metric => (
            <MetricBarChart buckets={largestFirst(data.types, metric)} metric={metric} orientation="horizontal"
              emptyLabel={i18next.t('analytics:noType')} colorOf={typeColorOf} onSelect={selectType} />
          )}
        </ChartCard>
      </div>
      <p className={styles.note}>{i18next.t('analytics:investmentNote')}</p>
    </div>
  );
};

export default AnalyticsCharts;
